import type { CanonicalTemplateGeometryResult, GeometryOrientation, CanonicalTemplateAxisEvidence, CanonicalWarpResolutionBenchmark } from "../types/liveGeometry";
import {
  VECTOR_TEMPLATE_HORIZONTAL_LINES,
  VECTOR_TEMPLATE_VERTICAL_LINES,
  VECTOR_TEMPLATE_VERSION,
  VECTOR_TEMPLATE_SOURCE_SHA256,
  VECTOR_TEMPLATE_CONTRACT_SHA256,
} from "../generated/vectorTemplateContract";

export const CANONICAL_TEMPLATE_GEOMETRY_CONTRACT_VERSION = "canonical-template-geometry-v1" as const;
export const CANONICAL_TEMPLATE_GEOMETRY_POLICY_VERSION = "canonical-template-geometry-policy-v1" as const;
export const CANONICAL_TEMPLATE_GEOMETRY_VERSION = VECTOR_TEMPLATE_VERSION;

export const CANONICAL_LABEL_NOMINAL_SIZE_MM = Object.freeze({ width: 100, height: 24 });
export const CANONICAL_WARP_RESOLUTION_CANDIDATES = Object.freeze([
  { width: 1200, height: 288 },
  { width: 1500, height: 360 },
  { width: 1800, height: 432 },
] as const);
export const CANONICAL_WARP_REFERENCE_RESOLUTION = Object.freeze({ width: 1800, height: 432 });

// SR26: exakte Rastergeometrie stammt aus dem autoritativen Blanko-Vektor.
const HORIZONTAL_LINES = VECTOR_TEMPLATE_HORIZONTAL_LINES;
const VERTICAL_LINES = VECTOR_TEMPLATE_VERTICAL_LINES;

type ImageDataLike = { width: number; height: number; data: Uint8ClampedArray | Uint8Array };
type OrientationProbe = {
  orientation: "ROTATION_0" | "ROTATION_180";
  residual: number;
  score: number;
  frameSupport: number;
  internalSupport: number;
  horizontal: CanonicalTemplateAxisEvidence[];
  vertical: CanonicalTemplateAxisEvidence[];
};

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const grayAt = (image: ImageDataLike, x: number, y: number) => {
  const safeX = Math.max(0, Math.min(image.width - 1, Math.round(x)));
  const safeY = Math.max(0, Math.min(image.height - 1, Math.round(y)));
  const index = (safeY * image.width + safeX) * 4;
  return Number(image.data[index] ?? 0) * 0.299
    + Number(image.data[index + 1] ?? 0) * 0.587
    + Number(image.data[index + 2] ?? 0) * 0.114;
};

function horizontalStrength(image: ImageDataLike, y: number): number {
  if (image.width < 8 || image.height < 8) return 0;
  const yy = Math.max(1, Math.min(image.height - 2, y));
  const samples = Math.max(48, Math.min(160, Math.round(image.width / 8)));
  let total = 0;
  for (let index = 0; index < samples; index += 1) {
    const x = 2 + (image.width - 5) * (index / Math.max(1, samples - 1));
    total += Math.abs(grayAt(image, x, yy + 1) - grayAt(image, x, yy - 1)) / 255;
  }
  return total / samples;
}

function verticalStrength(image: ImageDataLike, x: number): number {
  if (image.width < 8 || image.height < 8) return 0;
  const xx = Math.max(1, Math.min(image.width - 2, x));
  const samples = Math.max(32, Math.min(96, Math.round(image.height / 5)));
  let total = 0;
  for (let index = 0; index < samples; index += 1) {
    const y = 2 + (image.height - 5) * (index / Math.max(1, samples - 1));
    total += Math.abs(grayAt(image, xx + 1, y) - grayAt(image, xx - 1, y)) / 255;
  }
  return total / samples;
}

function probeAxis(
  image: ImageDataLike,
  expectedLines: readonly number[],
  axis: "horizontal" | "vertical",
): CanonicalTemplateAxisEvidence[] {
  const dimension = axis === "horizontal" ? image.height : image.width;
  const radius = Math.max(2, Math.round(dimension * 0.025));
  const strengthAt = axis === "horizontal" ? horizontalStrength : verticalStrength;
  return expectedLines.map((expected, index) => {
    const center = Math.round(expected * Math.max(1, dimension - 1));
    let bestCoordinate = center;
    let bestStrength = -1;
    for (let offset = -radius; offset <= radius; offset += 1) {
      const coordinate = Math.max(1, Math.min(dimension - 2, center + offset));
      const strength = strengthAt(image, coordinate);
      if (strength > bestStrength) {
        bestStrength = strength;
        bestCoordinate = coordinate;
      }
    }
    return {
      expected,
      observed: bestCoordinate / Math.max(1, dimension - 1),
      residual_normalized: Math.abs(bestCoordinate - center) / Math.max(1, dimension - 1),
      edge_strength: Math.max(0, bestStrength),
      frame_line: index === 0 || index === expectedLines.length - 1,
    };
  });
}

function probeOrientation(image: ImageDataLike, orientation: "ROTATION_0" | "ROTATION_180"): OrientationProbe {
  const transform = (lines: readonly number[]) => orientation === "ROTATION_0"
    ? [...lines]
    : [...lines].map((value) => 1 - value).sort((a, b) => a - b);
  const horizontal = probeAxis(image, transform(HORIZONTAL_LINES), "horizontal");
  const vertical = probeAxis(image, transform(VERTICAL_LINES), "vertical");
  const all = [...horizontal, ...vertical];
  const internal = all.filter((entry) => !entry.frame_line);
  const frame = all.filter((entry) => entry.frame_line);
  const residual = internal.reduce((sum, entry) => sum + entry.residual_normalized, 0) / Math.max(1, internal.length);
  const internalStrength = internal.reduce((sum, entry) => sum + entry.edge_strength, 0) / Math.max(1, internal.length);
  const frameStrength = frame.reduce((sum, entry) => sum + entry.edge_strength, 0) / Math.max(1, frame.length);
  const internalSupport = clamp01(internalStrength / 0.16);
  const frameSupport = clamp01(frameStrength / 0.18);
  const residualScore = clamp01(1 - residual / 0.025);
  const score = clamp01(residualScore * 0.56 + internalSupport * 0.34 + frameSupport * 0.10);
  return { orientation, residual, score, frameSupport, internalSupport, horizontal, vertical };
}

export function benchmarkCanonicalWarpResolutions(residualNormalized: number | null): CanonicalWarpResolutionBenchmark[] {
  return CANONICAL_WARP_RESOLUTION_CANDIDATES.map((candidate) => ({
    width: candidate.width,
    height: candidate.height,
    projected_residual_px: residualNormalized === null
      ? null
      : Math.round(residualNormalized * Math.hypot(candidate.width, candidate.height) * 100) / 100,
    rgba_bytes: candidate.width * candidate.height * 4,
    reference_resolution: candidate.width === CANONICAL_WARP_REFERENCE_RESOLUTION.width
      && candidate.height === CANONICAL_WARP_REFERENCE_RESOLUTION.height,
  }));
}

export function evaluateCanonicalTemplateGeometry(image: ImageDataLike): CanonicalTemplateGeometryResult {
  if (!Number.isFinite(image.width) || !Number.isFinite(image.height) || image.width < 32 || image.height < 16) {
    throw new Error("SR24 Canonical Template: ungültige Bildabmessungen.");
  }
  if (!image.data || image.data.length < image.width * image.height * 4) {
    throw new Error("SR24 Canonical Template: unvollständige RGBA-Daten.");
  }

  const zero = probeOrientation(image, "ROTATION_0");
  const halfTurn = probeOrientation(image, "ROTATION_180");
  const best = halfTurn.score > zero.score ? halfTurn : zero;
  const second = best === zero ? halfTurn : zero;
  const orientationMargin = Math.max(0, best.score - second.score);
  const strongEvidence = best.internalSupport >= 0.42;
  const strongMismatch = strongEvidence && best.residual > 0.021 && best.score < 0.48;
  const confirmed = strongEvidence && best.residual <= 0.018 && best.score >= 0.50;
  const decision: CanonicalTemplateGeometryResult["decision"] = strongMismatch
    ? "REJECTED_STRONG_MISMATCH"
    : confirmed
      ? "CONFIRMED"
      : "INCONCLUSIVE";
  const orientation: GeometryOrientation = orientationMargin >= 0.055 ? best.orientation : "UNKNOWN";

  return {
    contract_version: CANONICAL_TEMPLATE_GEOMETRY_CONTRACT_VERSION,
    policy_version: CANONICAL_TEMPLATE_GEOMETRY_POLICY_VERSION,
    template_version: CANONICAL_TEMPLATE_GEOMETRY_VERSION,
    template_source: "AUTHORITATIVE_BLANK_VECTOR",
    vector_template_parse_applied: true,
    nominal_width_mm: 100,
    nominal_height_mm: 24,
    decision,
    accepted: decision !== "REJECTED_STRONG_MISMATCH",
    // SR24 sammelt/benchmarkiert die harte Template-Evidenz zunächst additiv.
    // Ein starker Mismatch wird persistiert, aber erst nach Device-Benchmark als
    // Production-Hard-Reject aktiviert. So bleibt Full Detection der sichere Pfad.
    hard_reject_applied: false,
    orientation,
    orientation_margin: orientationMargin,
    template_residual_normalized: best.residual,
    template_score: best.score,
    frame_support: best.frameSupport,
    internal_line_support: best.internalSupport,
    horizontal: best.horizontal,
    vertical: best.vertical,
    selected_resolution: { width: 1800, height: 432 },
    resolution_benchmark: benchmarkCanonicalWarpResolutions(best.residual),
    reason: decision === "CONFIRMED"
      ? "PRINTED_FRAME_TEMPLATE_CONFIRMED"
      : decision === "REJECTED_STRONG_MISMATCH"
        ? "STRONG_TEMPLATE_MISMATCH_RECORDED_NO_HARD_REJECT_UNTIL_DEVICE_BENCHMARK"
        : "TEMPLATE_EVIDENCE_INCONCLUSIVE_KEEP_EXISTING_PRODUCTION_RESULT",
    provenance: {
      sr24: true,
      exact_vector_template_deferred_to_sr26: false,
      vector_template_source_sha256: VECTOR_TEMPLATE_SOURCE_SHA256,
      vector_template_contract_sha256: VECTOR_TEMPLATE_CONTRACT_SHA256,
      raw_projected_aspect_hard_gate_used: false,
      orientation_applied_to_output: false,
    },
  };
}
